import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import MainMenu from '@/components/MainMenu';
import SeoHead from '@/components/SeoHead';
import { publishedResourcePages, resourcePagesBySlug } from '@/data/resourcePages';

function renderInlineContent(text, keyPrefix) {
  if (!text) return null;

  const pattern = /(\*\*([^*]+)\*\*|\[([^\]]+)\]\((\/[^)\s]+)\))/g;
  const parts = [];
  let lastIndex = 0;
  let match = pattern.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      parts.push(<span key={`${keyPrefix}-text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
    }

    if (match[1].startsWith('**')) {
      parts.push(
        <strong key={`${keyPrefix}-strong-${match.index}`}>{match[2]}</strong>
      );
    } else {
      const [, , , label, href] = match;
      parts.push(
        <Link
          key={`${keyPrefix}-link-${match.index}`}
          to={href}
          className="font-medium text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:text-primary-hover hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:rounded-sm"
        >
          {label}
        </Link>
      );
    }

    lastIndex = pattern.lastIndex;
    match = pattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(<span key={`${keyPrefix}-text-${lastIndex}`}>{text.slice(lastIndex)}</span>);
  }

  if (parts.length === 0) {
    return <span>{text}</span>;
  }

  return <>{parts}</>;
}

function renderFigure(figure, keyPrefix) {
  return (
    <figure key={keyPrefix} className="my-8 max-w-full overflow-hidden rounded-2xl border border-border bg-surface p-2 shadow-sm">
      <img
        src={figure.src}
        alt={figure.alt}
        loading="lazy"
        decoding="async"
        width={figure.width}
        height={figure.height}
        className="h-auto w-full max-w-full rounded-xl object-contain"
      />
    </figure>
  );
}

function isValidContentTable(table) {
  return Array.isArray(table?.headers)
    && table.headers.length > 0
    && table.headers.every((header) => typeof header === 'string' && header.trim())
    && Array.isArray(table.rows)
    && table.rows.every(
      (row) => Array.isArray(row)
        && row.length === table.headers.length
        && row.every((cell) => typeof cell === 'string' || typeof cell === 'number')
    );
}

function renderContentTable(table, keyPrefix) {
  if (!isValidContentTable(table)) return null;

  return (
    <div
      key={keyPrefix}
      className="my-8 max-w-full overflow-x-auto rounded-2xl border border-border bg-white shadow-sm"
      aria-label={table.caption || 'Resource information table'}
    >
      <table className="w-full min-w-[42rem] border-collapse text-left text-sm leading-6 text-muted-foreground">
        {table.caption && (
          <caption className="caption-top px-5 py-4 text-left text-base font-semibold text-foreground">
            {renderInlineContent(table.caption, `${keyPrefix}-caption`)}
          </caption>
        )}
        <thead className="bg-surface text-foreground">
          <tr>
            {table.headers.map((header, headerIndex) => (
              <th
                key={`${keyPrefix}-header-${headerIndex}`}
                scope="col"
                className="border-b border-border px-5 py-3 font-semibold"
              >
                {renderInlineContent(header, `${keyPrefix}-header-${headerIndex}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={`${keyPrefix}-row-${rowIndex}`} className="border-b border-border last:border-b-0">
              {row.map((cell, cellIndex) => (
                <td key={`${keyPrefix}-cell-${rowIndex}-${cellIndex}`} className="px-5 py-3 align-top">
                  {renderInlineContent(String(cell), `${keyPrefix}-cell-${rowIndex}-${cellIndex}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderContentTableWithAnchoredFigures(table, keyPrefix, contentFigures) {
  const renderedTable = renderContentTable(table, keyPrefix);
  if (!renderedTable) return [];

  const figuresAfterTable = contentFigures.filter(
    (figure) => typeof figure?.afterTableCaption === 'string'
      && figure.afterTableCaption === table.caption
  );

  return [
    renderedTable,
    ...figuresAfterTable.map((figure, figureIndex) => renderFigure(
      figure,
      `${keyPrefix}-figure-${figureIndex}-${figure.src}`
    ))
  ];
}

function getVisibleInlineText(text) {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\(\/[^)\s]+\)/g, '$1');
}

function getBlockLines(block) {
  return block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function parseOrderedItemBlock(block) {
  const lines = getBlockLines(block);
  if (!lines.length) return null;

  const itemMatch = lines[0].match(/^(?:\*\*)?(\d+)\.\s+(.+)$/);
  if (!itemMatch) return null;

  return {
    rawNumber: itemMatch[1],
    line: lines[0],
    nestedLines: lines.slice(1)
  };
}

function renderOrderedItemIntro(line, keyPrefix) {
  const boldItemMatch = line.match(/^\*\*(\d+)\.\s+(.+?)\*\*\s*(.*)$/);
  if (boldItemMatch) {
    const [, , label, rest] = boldItemMatch;
    const trimmedRest = rest.trim();

    return (
      <>
        <strong>{label}</strong>
        {trimmedRest ? (
          <>
            {' '}
            {renderInlineContent(trimmedRest, `${keyPrefix}-rest`)}
          </>
        ) : null}
      </>
    );
  }

  const plainItemMatch = line.match(/^\d+\.\s+(.+)$/);
  const bodyText = plainItemMatch ? plainItemMatch[1].trim() : line;
  return renderInlineContent(bodyText, keyPrefix);
}

function renderNestedListItemContent(lines, keyPrefix) {
  const elements = [];
  let paragraphLines = [];
  let bulletLines = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;

    const elementIndex = elements.length;
    elements.push(
      <p key={`${keyPrefix}-paragraph-${elementIndex}`} className="mt-3">
        {renderInlineContent(paragraphLines.join(' '), `${keyPrefix}-paragraph-${elementIndex}`)}
      </p>
    );
    paragraphLines = [];
  };

  const flushBulletList = () => {
    if (!bulletLines.length) return;

    const elementIndex = elements.length;
    elements.push(
      <ul key={`${keyPrefix}-bullets-${elementIndex}`} className="mt-3 list-disc space-y-2 pl-5">
        {bulletLines.map((line, lineIndex) => {
          const cleanedLine = line.replace(/^[-*]\s+/, '');
          return (
            <li key={`${keyPrefix}-bullet-${lineIndex}`}>
              {renderInlineContent(cleanedLine, `${keyPrefix}-bullet-${lineIndex}`)}
            </li>
          );
        })}
      </ul>
    );
    bulletLines = [];
  };

  lines.forEach((line) => {
    if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      bulletLines.push(line);
      return;
    }

    flushBulletList();
    paragraphLines.push(line);
  });

  flushParagraph();
  flushBulletList();

  return elements;
}

function renderContentBlocks(content, contentFigures = [], contentTables = []) {
  const rawBlocks = content
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  const elements = [];
  let pendingFigures = [];

  const flushPendingFigures = () => {
    pendingFigures.forEach((figure) => {
      elements.push(renderFigure(figure, `figure-${figure.src}`));
    });
    pendingFigures = [];
  };

  for (let index = 0; index < rawBlocks.length; index += 1) {
    const block = rawBlocks[index];
    const lines = getBlockLines(block);

    if (!lines.length) continue;

    const headingMatch = lines[0].match(/^(#{1,3})\s+(.*)$/);
    const headingLevel = headingMatch ? (headingMatch[1].length === 3 ? 3 : 2) : null;
    const headingText = headingMatch ? headingMatch[2].trim() : null;

    if (pendingFigures.length && headingLevel) {
      flushPendingFigures();
    }

    if (headingMatch) {
      const headingClass = headingLevel === 2
        ? 'mt-10 text-2xl font-semibold text-foreground first:mt-0'
        : 'mt-8 text-xl font-semibold text-foreground first:mt-0';
      const HeadingTag = headingLevel === 2 ? 'h2' : 'h3';
      elements.push(
        <HeadingTag key={`heading-${index}`} className={headingClass}>
          {headingText}
        </HeadingTag>
      );

      pendingFigures = contentFigures.filter((figure) => figure.afterHeading === headingText);
      contentTables
        .filter((table) => table.afterHeading === headingText)
        .forEach((table, tableIndex) => {
          elements.push(
            ...renderContentTableWithAnchoredFigures(
              table,
              `table-heading-${headingText}-${tableIndex}`,
              contentFigures
            )
          );
        });
      continue;
    }

    const orderedItem = parseOrderedItemBlock(block);
    if (orderedItem) {
      const orderedItems = [];
      let cursor = index;

      while (cursor < rawBlocks.length) {
        const nextOrderedItem = parseOrderedItemBlock(rawBlocks[cursor]);
        if (!nextOrderedItem) break;

        orderedItems.push({
          ...nextOrderedItem,
          blockIndex: cursor
        });
        cursor += 1;
      }

      const renderOrderedList = (items, listKey) => (
        <ol key={listKey} className="mt-6 list-decimal space-y-3 pl-6 text-base leading-8 text-muted-foreground">
          {items.map((item, itemIndex) => (
            <li key={`${item.rawNumber}-${itemIndex}`} value={Number(item.rawNumber)} className="pl-2">
              <div>{renderOrderedItemIntro(item.line, `${item.blockIndex}-${itemIndex}`)}</div>
              {renderNestedListItemContent(item.nestedLines, `${item.blockIndex}-${itemIndex}-nested`)}
            </li>
          ))}
        </ol>
      );

      let listStartIndex = 0;
      orderedItems.forEach((item, itemIndex) => {
        const figuresAfterItem = contentFigures.filter(
          (figure) => Number(figure.afterItem) === Number(item.rawNumber)
        );
        const tablesAfterItem = contentTables.filter(
          (table) => Number(table.afterItem) === Number(item.rawNumber)
        );

        if (!figuresAfterItem.length && !tablesAfterItem.length) return;

        const listItems = orderedItems.slice(listStartIndex, itemIndex + 1);
        elements.push(renderOrderedList(listItems, `ordered-list-${index}-${listStartIndex}`));
        figuresAfterItem.forEach((figure) => {
          elements.push(renderFigure(figure, `figure-${figure.src}`));
        });
        tablesAfterItem.forEach((table, tableIndex) => {
          elements.push(
            ...renderContentTableWithAnchoredFigures(
              table,
              `table-item-${item.rawNumber}-${tableIndex}`,
              contentFigures
            )
          );
        });
        listStartIndex = itemIndex + 1;
      });

      if (listStartIndex < orderedItems.length) {
        elements.push(renderOrderedList(orderedItems.slice(listStartIndex), `ordered-list-${index}-${listStartIndex}`));
      }
      index = cursor - 1;
      continue;
    }

    if (lines.every((line) => /^[-*]\s+/.test(line))) {
      elements.push(
        <ul key={`unordered-list-${index}`} className="mt-6 list-disc space-y-2 pl-6 text-base leading-8 text-muted-foreground">
          {lines.map((line, lineIndex) => {
            const cleanedLine = line.replace(/^[-*]\s+/, '');
            return <li key={`${index}-${lineIndex}`}>{renderInlineContent(cleanedLine, `${index}-${lineIndex}`)}</li>;
          })}
        </ul>
      );
      continue;
    }

    const paragraphText = lines.join(' ');
    elements.push(
      <p key={`paragraph-${index}`} className="mt-6 text-base leading-8 text-muted-foreground first:mt-0">
        {renderInlineContent(paragraphText, index)}
      </p>
    );
    contentFigures
      .filter(
        (figure) => typeof figure?.afterParagraphText === 'string'
          && figure.afterParagraphText === getVisibleInlineText(paragraphText)
      )
      .forEach((figure, figureIndex) => {
        elements.push(renderFigure(figure, `figure-paragraph-${index}-${figureIndex}-${figure.src}`));
      });
  }

  flushPendingFigures();
  return elements;
}

export default function ResourcePage() {
  const { slug } = useParams();
  const page = slug ? resourcePagesBySlug[slug] : null;
  const canonicalPath = slug ? `/resources/${slug}` : '/resources';

  const contentBlocks = useMemo(
    () => (page ? renderContentBlocks(page.content, page.content_figures || [], page.content_tables || []) : []),
    [page]
  );
  const relatedPages = useMemo(() => {
    if (page?.related_slugs?.length) {
      const resolvedRelatedPages = page.related_slugs
        .map((relatedSlug) => publishedResourcePages.find((candidate) => candidate.slug === relatedSlug && candidate.published))
        .filter(Boolean)
        .filter((candidate) => candidate.slug !== page.slug)
        .filter((candidate, index, candidates) => candidates.findIndex((item) => item.slug === candidate.slug) === index)
        .slice(0, 4);

      if (resolvedRelatedPages.length > 0) {
        return resolvedRelatedPages;
      }
    }

    const currentPageIndex = publishedResourcePages.findIndex((candidate) => candidate.slug === slug);

    if (currentPageIndex === -1 || publishedResourcePages.length <= 1) {
      return [];
    }

    const maxRelatedPages = Math.min(3, publishedResourcePages.length - 1);

    return Array.from({ length: maxRelatedPages }, (_, offset) => {
      const nextIndex = (currentPageIndex + offset + 1) % publishedResourcePages.length;
      return publishedResourcePages[nextIndex];
    });
  }, [page, slug]);
  const relatedCards = useMemo(() => {
    const resourceCards = relatedPages.map((relatedPage) => ({
      key: `resource-${relatedPage.slug}`,
      to: `/resources/${relatedPage.slug}`,
      title: relatedPage.title,
      excerpt: relatedPage.excerpt || relatedPage.meta_description || 'Published JOBFITAI resource page.'
    }));
    const relatedLinks = (page?.related_links || [])
      .filter((link) => link
        && typeof link.to === 'string'
        && link.to.startsWith('/')
        && typeof link.title === 'string'
        && link.title.trim())
      .filter((link, index, links) => links.findIndex((item) => item.to === link.to) === index)
      .filter((link) => link.to !== `/resources/${page.slug}`)
      .map((link) => ({
        key: `link-${link.to}`,
        to: link.to,
        title: link.title,
        excerpt: link.excerpt || 'Published JOBFITAI resource page.'
      }));

    return [...resourceCards, ...relatedLinks].slice(0, 4);
  }, [page, relatedPages]);

  const topDownloads = useMemo(() => (page?.downloads || []).filter((download) => download.position === 'top'), [page]);
  const bottomDownloads = useMemo(() => (page?.downloads || []).filter((download) => download.position === 'bottom'), [page]);

  if (!page?.published) {
    return (
      <div className="min-h-screen bg-surface">
        <SeoHead
          title="Resource Page Not Found | JOBFITAI"
          description="This JOBFITAI resource page could not be found."
          canonicalPath={canonicalPath}
          robots="noindex,follow"
        />
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="mb-8 flex items-center gap-3">
            <MainMenu />
            <Link to="/resources" className="text-primary hover:text-primary-hover transition-colors">
              Back to Resources
            </Link>
          </div>
          <div className="rounded-3xl border border-border bg-white px-8 py-16 shadow-soft text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground">Resource Page Not Found</h1>
            <p className="mt-4 text-muted-foreground">
              This resource page does not exist.
            </p>
            <div className="mt-6 flex items-center justify-center gap-4">
              <Link
                to="/resources"
                className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-hover"
              >
                Browse Resources
              </Link>
              <Link
                to="/"
                className="inline-flex items-center rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-surface"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <SeoHead
        title={page.meta_title || page.title}
        description={page.meta_description || page.excerpt || ''}
        canonicalPath={`/resources/${page.slug}`}
        robots="index,follow"
      />
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
        <div className="mb-8 flex items-center gap-3">
          <MainMenu />
          <Link to="/resources" className="text-primary hover:text-primary-hover transition-colors">
            Back to Resources
          </Link>
        </div>

        <article className="rounded-3xl border border-border bg-white p-8 shadow-soft md:p-12">
          <header className="mb-10 border-b border-border pb-8">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              Resource Page
            </p>
            <h1 className="mt-4 font-heading text-4xl font-bold leading-tight text-foreground md:text-5xl">
              {page.title}
            </h1>
            {(page.excerpt || page.meta_description) && (
              <p className="mt-4 max-w-3xl text-lg leading-8 text-muted-foreground">
                {page.excerpt || page.meta_description}
              </p>
            )}
            {page.last_reviewed && (
              <p className="mt-4 text-sm font-medium text-muted-foreground">
                Last reviewed: {page.last_reviewed}
              </p>
            )}
          </header>

          {topDownloads.length > 0 && (
            <div className="mb-8 flex flex-col gap-3 sm:flex-row">
              {topDownloads.map((download) => (
                <a
                  key={`${download.position}-${download.label}`}
                  href={download.url}
                  download={download.download ? true : undefined}
                  className="inline-flex items-center justify-center rounded-lg border border-primary bg-white px-5 py-3 text-sm font-semibold text-primary transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {download.label}
                </a>
              ))}
            </div>
          )}

          <div className="max-w-none">
            {contentBlocks}
          </div>

          {page?.cta && (
            <section className="mt-10 border-t border-border pt-8">
              {page.cta.disabled ? (
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-white opacity-60 cursor-not-allowed"
                >
                  {page.cta.label}
                </button>
              ) : (
                <Link
                  to={page.cta.to}
                  className="inline-flex items-center rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-hover"
                >
                  {page.cta.label}
                </Link>
              )}
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/" className="text-sm font-medium text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:text-primary-hover">
                  Learn about JobFitAI
                </Link>
                <Link to="/pricing" className="text-sm font-medium text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:text-primary-hover">
                  View plans
                </Link>
              </div>
            </section>
          )}

          {bottomDownloads.length > 0 && (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {bottomDownloads.map((download) => (
                <a
                  key={`${download.position}-${download.label}`}
                  href={download.url}
                  download={download.download ? true : undefined}
                  className="inline-flex items-center justify-center rounded-lg border border-primary bg-white px-5 py-3 text-sm font-semibold text-primary transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {download.label}
                </a>
              ))}
            </div>
          )}

          {relatedCards.length > 0 && (
            <section className="mt-12 border-t border-border pt-8">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-foreground">Related Resources</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Explore more resume and job-description guides from JOBFITAI.
                  </p>
                </div>
                <Link
                  to="/resources"
                  className="text-sm font-medium text-primary transition-colors hover:text-primary-hover"
                >
                  Browse all resources
                </Link>
              </div>
              <div className={`mt-6 grid gap-4 ${relatedCards.length === 4 ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3'}`}>
                {relatedCards.map((relatedCard) => (
                  <Link
                    key={relatedCard.key}
                    to={relatedCard.to}
                    className="rounded-2xl border border-border bg-surface px-5 py-4 transition-colors hover:border-primary/30 hover:bg-white"
                  >
                    <h3 className="font-heading text-lg font-semibold text-foreground">
                      {relatedCard.title}
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-muted-foreground">
                      {relatedCard.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
    </div>
  );
}
